import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";
import { useAuth } from "@/context/AuthContext";
import { Lock, Loader2, CheckCircle2, AlertTriangle } from "lucide-react";

const inputCls =
  "w-full border border-slate-200 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#226DB4]/30 focus:border-[#226DB4] bg-white";

const Rule = ({ ok, label }) => (
  <li className={`flex items-center gap-2 ${ok ? "text-emerald-600" : "text-slate-400"}`}>
    <CheckCircle2 className={`h-3.5 w-3.5 ${ok ? "opacity-100" : "opacity-30"}`} />
    {label}
  </li>
);

export default function ChangePassword() {
  const { user, clearMustChangePassword } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const forced = location.state?.forced || user?.must_change_password;
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");

  const rules = {
    length: next.length >= 8,
    upper: /[A-Z]/.test(next),
    digit: /\d/.test(next),
    special: /[^a-zA-Z0-9]/.test(next),
    match: next.length > 0 && next === confirm,
    different: current && next && current !== next,
  };
  const allOk = Object.values(rules).every(Boolean);

  const formatErr = (d) => {
    if (!d) return "Failed to update password";
    if (typeof d === "string") return d;
    if (Array.isArray(d)) return d.map((e) => e.msg).join(" · ");
    return d.msg || JSON.stringify(d);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setOk("");
    if (!allOk) {
      setErr("Please meet all password requirements");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/change-password", {
        current_password: current,
        new_password: next,
      });
      setOk("Password updated successfully");
      setCurrent("");
      setNext("");
      setConfirm("");
      clearMustChangePassword();
      if (forced) {
        const landing = user?.role === "admin" ? "/dashboard" : "/dashboard";
        setTimeout(() => navigate(landing, { replace: true }), 800);
      }
    } catch (ex) {
      setErr(formatErr(ex?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AdminLayout title="Change Password" breadcrumbs={["Home", "Self Service", "Password"]}>
      <div className="max-w-xl">
        {forced && (
          <div
            data-testid="forced-banner"
            className="mb-4 bg-amber-50 border-l-4 border-amber-400 text-amber-800 px-4 py-3 rounded flex items-start gap-3"
          >
            <AlertTriangle className="h-5 w-5 mt-0.5 flex-shrink-0" />
            <div className="text-sm">
              <div className="font-semibold">First-login security check</div>
              <div className="text-amber-700">
                Please set a new password before accessing the rest of the application.
              </div>
            </div>
          </div>
        )}
        <div className="bg-white border border-slate-200 rounded shadow-sm">
          <div className="px-5 py-3.5 border-b border-slate-200 flex items-center gap-2">
            <Lock className="h-5 w-5 text-[#226DB4]" />
            <h3 className="text-base font-semibold text-slate-800">Update your password</h3>
          </div>
          <form onSubmit={onSubmit} className="p-6 space-y-4" data-testid="change-password-form">
            <div>
              <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1.5">
                Current password
              </label>
              <input
                type="password"
                required
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
                className={inputCls}
                data-testid="current-password-input"
                autoComplete="current-password"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1.5">
                New password
              </label>
              <input
                type="password"
                required
                value={next}
                onChange={(e) => setNext(e.target.value)}
                className={inputCls}
                data-testid="new-password-input"
                autoComplete="new-password"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1.5">
                Confirm new password
              </label>
              <input
                type="password"
                required
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className={inputCls}
                data-testid="confirm-password-input"
                autoComplete="new-password"
              />
            </div>

            <ul className="text-xs space-y-1 bg-slate-50 border border-slate-100 rounded p-3" data-testid="password-rules">
              <Rule ok={rules.length} label="At least 8 characters" />
              <Rule ok={rules.upper} label="Contains an uppercase letter" />
              <Rule ok={rules.digit} label="Contains a digit" />
              <Rule ok={rules.special} label="Contains a special character" />
              <Rule ok={rules.match} label="New password matches confirmation" />
              <Rule ok={rules.different} label="New password differs from current" />
            </ul>

            {err && (
              <div className="bg-rose-50 border border-rose-200 text-rose-700 px-3 py-2 rounded text-sm" data-testid="cp-error">
                {err}
              </div>
            )}
            {ok && (
              <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 px-3 py-2 rounded text-sm" data-testid="cp-success">
                {ok}
              </div>
            )}

            <button
              type="submit"
              disabled={!allOk || busy}
              data-testid="cp-submit-btn"
              className="inline-flex items-center gap-2 px-5 py-2 text-sm bg-[#226DB4] hover:bg-[#1B5A94] text-white rounded font-medium disabled:opacity-60"
            >
              {busy && <Loader2 className="h-4 w-4 animate-spin" />}
              Update Password
            </button>
          </form>
        </div>

        <div className="mt-4 text-xs text-slate-500 leading-relaxed">
          🔒 For your account security, please change your default password (<code className="bg-slate-100 px-1 rounded">Welcome@123</code>)
          on first login. We rate-limit login attempts and password changes to protect against brute force attacks.
        </div>
      </div>
    </AdminLayout>
  );
}
